import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  NgZone,
  Optional,
  Output,
  Renderer2,
  Type,
  ViewChild,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { PageConfigAction } from '@core/models/page-config.model';
import { Sidebar } from 'primeng/sidebar';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { delay, filter, first, map } from 'rxjs/operators';
import { DynamicDialogcontentDirective } from '@capturum/ui/api';
import { SidebarAction } from '@core/enums/sidebar-actions.enum';
import { Observable, Subscription } from 'rxjs';
import { NavMenuItem } from '@core/models/nav-menu-item.model';
import { MenuItemService } from '@shared/services/menu-item.service';
import { FontAwesomeIcon } from '@core/enums/font-awesome-icon.enum';
import { DialogSubmitService } from '@shared/services/dialog-submit.service';

@Component({
  selector: 'app-right-sidebar-int',
  templateUrl: './right-sidebar.component.html',
  styleUrls: ['./right-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RightSidebarComponent implements AfterViewInit, OnDestroy {
  @Input()
  public title: string;
  @Input()
  public title$: Observable<string>;
  @Input()
  public actions: PageConfigAction[];
  @Input()
  public visible: boolean;
  @Input()
  public showButtons = true;
  @Input()
  public showSendButton = false;
  @Input()
  public submitButtonTranslationKey = 'button.submit';
  @Input()
  public cancelButtonTranslationKey = 'button.cancel';
  @Input()
  public submitButtonDisabled = false;
  @Input()
  public submitButtonDisabled$: Observable<boolean>;
  @Input()
  public submitButtonStyleClass = 'success';
  @Input()
  public sendButtonStyleClass = 'primary';
  @Input()
  public submitButtonIcon: string = FontAwesomeIcon.Save;
  @Input()
  public showCross = true;

  @Output()
  public onSubmit = new EventEmitter<any>();
  @Output()
  public onCancel = new EventEmitter<any>();
  @Output()
  public visibleChange = new EventEmitter<boolean>();
  @Output()
  public onHide = new EventEmitter<void>();
  public fontAwesomeIcon = FontAwesomeIcon;

  @ViewChild(Sidebar, { static: true }) set sidebarRef(ref: any) {
    if (ref) {
      this.sidebar = ref;
      this.subscription = this.sidebar.visibleChange?.subscribe((visible) => {
        this.visibleChange.emit(visible);
        this.visible = visible;
      });
    }
  }

  public sidebar: Sidebar;

  @ViewChild(DynamicDialogcontentDirective)
  public insertionPoint: DynamicDialogcontentDirective;

  public componentRef: ComponentRef<any>;
  public childComponentType: Type<any>;
  public activeMenuItemKey$: Observable<string>;
  public dismissable = true;
  public subscription: Subscription;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private cd: ChangeDetectorRef,
    private menuItemService: MenuItemService,
    public renderer: Renderer2,
    public config: DynamicDialogConfig,
    @Optional() private dialogRef: DynamicDialogRef,
    public zone: NgZone,
    private dialogSubmitService: DialogSubmitService,
  ) {

    this.activeMenuItemKey$ = this.menuItemService.getActiveMenuItem().pipe(
      filter(Boolean),
      map((menuItem: NavMenuItem) => {
        return `module-${menuItem.key ?? 'manage'}`;
      }), delay(1));
  }

  public ngAfterViewInit(): void {

    if (this.childComponentType) {
      this.loadChildComponent(this.childComponentType);
      if (this.config && this.config.data) {
        this.title = this.config.header;
        this.title$ = !this.title ? this.config.data.title$ : null;
        this.showButtons = this.config.data.showButtons;
        this.showCross = this.config.data.showCross === undefined ? true : this.config.data.showCross;
        this.submitButtonStyleClass = this.config.data.submitButtonStyleClass || 'success';
        this.submitButtonIcon = this.config.data.submitButtonIcon || FontAwesomeIcon.Save;
        this.submitButtonTranslationKey = this.config.data.submitButtonTranslationKey || 'button.submit';
        this.showSendButton = this.config.data.showSendButton;
        this.submitButtonDisabled$ = this.config.data.submitButtonDisabled;
        if (this.config.dismissableMask !== undefined) {
          this.dismissable = this.config.dismissableMask;
        }
      }
      this.cd.detectChanges();

      // Make sure the animation is triggered for opening the sidebar
      setTimeout(() => {
        this.visible = true;
        this.visibleChange.emit(true);
        this.cd.detectChanges();
      }, 50);
    }
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public cancel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onCancel.emit();
    document.querySelector('.p-component-overlay')?.remove();
    if (this.dialogRef) {
      this.dialogRef.close(SidebarAction.cancel);
    }
  }

  public submit(): void {
    this.onSubmit.emit();
    if (!this.dialogSubmitService.hasSubscriptions && this.dialogRef) {
      this.dialogRef.close(SidebarAction.submit);
    } else {
        this.dialogSubmitService.emitSubmit();
    }
  }

  public hide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onHide.emit();
    if (this.dialogRef) {
      this.dialogRef.close(SidebarAction.cancel);
    }

    if (this.childComponentType) {
      // Wait for the animation to finish
      setTimeout(() => {
        this.componentRef.destroy();
        this.dialogRef?.destroy();
      }, 300);
    }
    document.querySelector('.p-component-overlay')?.remove();
  }

  public close(): void {
    this.dialogRef.onClose.pipe(
      filter((action) => action === SidebarAction.close),
      first(),
    );
  }

  public loadChildComponent(componentType: Type<any>): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);
    const viewContainerRef = this.insertionPoint.viewContainerRef;

    viewContainerRef.clear();
    this.componentRef = viewContainerRef.createComponent(componentFactory);
  }
}
